import classNames from 'classnames';
import { Link, useParams } from 'react-router-dom';
import Tippy from '@tippyjs/react';

import { AdminUserRoleEnum, Agency, AgencyRepUserRoleEnum, SalesRepUserRoleEnum } from '__generated-api__';
import api from 'api';
import { useAbility, useAuthenticatedUser } from 'auth';
import { useQuery } from 'hooks/query';
import Icon from 'components/icon';
import { MainHero } from 'my-account/components/MainHero';
import { Block403Error } from 'my-account/pages/403page';
import { ErrorMessagesContent } from 'my-account/utils/error-handler';
import useDocumentTitle from 'hooks/useDocumentTitle';
import { DisplayPrice } from 'my-account/utils/currency';
import { getAgencySalesGoalReport } from 'my-account/utils/agency';
import { AgencyTabsNav } from './edit';

const SalesGoalProgressBar: React.VFC<{ percentGoalAchieved: number }> = ({ percentGoalAchieved }) => {
  if (!Number.isFinite(percentGoalAchieved)) {
    return null;
  }

  if (percentGoalAchieved < 0) {
    // percentGoalAchieved should never be negative but lets just not render if it is
    return null;
  }

  const progress = Math.min(percentGoalAchieved, 1);
  const progressOver = percentGoalAchieved > 1 ? (percentGoalAchieved - 1) / percentGoalAchieved : undefined;

  return (
    <div className="c-metrics-goal u-mt-spacer">
      <div className="c-metrics-goal__bar">
        <div className="c-metrics-goal__progress" style={{ width: `${(progress * 100).toFixed(2)}%` }}>
          {!!progressOver && (
            <div
              className="c-metrics-goal__progress-over"
              style={{ width: `${(progressOver * 100).toFixed(2)}%` }}
            ></div>
          )}
          <div className="c-metrics-goal__hotspot">
            {percentGoalAchieved < 1 ? (
              <Icon name="emoji-happy" className="o-svg-icon" />
            ) : (
              <Icon name="emoji-stars" className="o-svg-icon" />
            )}
          </div>
          <div className="c-metrics-goal__value">{(percentGoalAchieved * 100).toFixed(2)}%</div>
        </div>
      </div>
    </div>
  );
};

const AgencySalesGoalReport: React.VFC<{ agency: Agency }> = ({ agency }) => {
  const { goals } = agency;

  if (!goals || !goals.length) {
    return null;
  }

  const report = getAgencySalesGoalReport(goals);

  if (!report) {
    return null;
  }

  const {
    totalRevenue,
    lastFiscalYearRevenue,
    salesGoal,
    percentGoalAchieved,
    averageOrderSize,
    lastFiscalYearAverageOrderSize,
    orderSizeGrowthPercent,
    totalOrders,
    lastFiscalYearOrders,
  } = report;

  return (
    <section className="c-block c-block--spacing-t-extra-small c-block--spacing-b c-block--spacing-b-large@md">
      <div className="o-container-fluid">
        <div className="o-row">
          <div className="o-col-12">
            <div className="c-order-summary u-mb-spacer-section-small u-mb-spacer-section@md">
              <header className="c-order-summary__header c-order-summary__header--no-divider u-mb-spacer-base">
                <ul className="c-order-summary-info">
                  <li className="c-order-summary-info__column">
                    <p className="c-order-summary-info__title c-order-summary-info__title--larger">{agency.name}</p>

                    {agency.territory?.region?.region_name && (
                      <p className="c-order-summary-info__label">
                        <strong>{agency.territory.region.region_name}</strong>
                      </p>
                    )}
                  </li>
                </ul>

                <div className="c-listing__header">
                  {/* <IconDropdown
                    buttonLabel="Actions"
                    menuLabel="Actions"
                    options={Object.assign(
                      {},
                      {
                        action_01: 'Test Action 01',
                        action_02: 'Test Action 02',
                      }
                    )}
                    onChange={(value) => {
                      window.alert(`Test action: ${value}`);
                    }}
                  /> */}
                </div>
              </header>

              <div className="c-metrics-wrapper u-mb-spacer-base-large">
                <ul className="c-metrics c-metrics--wider-spacing@xl u-mb-0">
                  <li className="c-metrics__column u-flex-grow">
                    <p className="c-metrics-title">Sales</p>
                    <div className="c-metrics-data__wrapper">
                      <div className="c-metrics-data w-full w-auto@sm">
                        <p className="c-metrics-data__title">
                          <DisplayPrice value={totalRevenue} />
                        </p>
                        <p className="c-metrics-data__label">Current Year</p>
                      </div>
                      <div className="c-metrics-data c-metrics-data--minor">
                        <p className="c-metrics-data__title">
                          <DisplayPrice value={lastFiscalYearRevenue} />
                        </p>
                        <p className="c-metrics-data__label">
                          Last Year{' '}
                          <Tippy content="Full Year’s Performance">
                            <span className="c-tooltip">
                              <Icon name="help" className="o-svg-icon" />
                            </span>
                          </Tippy>
                        </p>
                      </div>
                      <div className="c-metrics-data c-metrics-data--minor c-metrics-data--end">
                        <p className="c-metrics-data__title">
                          <DisplayPrice value={salesGoal} />
                        </p>
                        <p className="c-metrics-data__label">Goal</p>
                      </div>
                    </div>

                    <SalesGoalProgressBar percentGoalAchieved={percentGoalAchieved} />
                  </li>

                  <li className="c-metrics__column">
                    <p className="c-metrics-title">Order Size</p>
                    <div className="c-metrics-data__wrapper">
                      <div className="c-metrics-data">
                        <p className="c-metrics-data__title">
                          <DisplayPrice value={averageOrderSize} />
                          {Number.isFinite(orderSizeGrowthPercent) && (
                            <span
                              className={classNames('c-metrics-data__percentage', {
                                'u-text-error': orderSizeGrowthPercent < 0,
                                'u-text-success': orderSizeGrowthPercent >= 0,
                              })}
                            >
                              <Icon
                                name="arrow"
                                className={classNames('o-svg-icon', {
                                  'o-svg-down': orderSizeGrowthPercent < 0,
                                  'o-svg-up': orderSizeGrowthPercent >= 0,
                                })}
                              />
                              {`${(orderSizeGrowthPercent * 100).toFixed(2)}%`}
                            </span>
                          )}
                        </p>
                        <p className="c-metrics-data__label">Average Order Size</p>
                      </div>
                      <div className="c-metrics-data c-metrics-data--minor">
                        <p className="c-metrics-data__title">
                          <DisplayPrice value={lastFiscalYearAverageOrderSize} />
                        </p>
                        <p className="c-metrics-data__label">
                          Last Year{' '}
                          <Tippy content="Full Year’s Performance">
                            <span className="c-tooltip">
                              <Icon name="help" className="o-svg-icon" />
                            </span>
                          </Tippy>
                        </p>
                      </div>
                    </div>
                  </li>

                  <li className="c-metrics__column u-flex-grow">
                    <p className="c-metrics-title">Order Number</p>
                    <div className="c-metrics-data__wrapper">
                      <div className="c-metrics-data">
                        <p className="c-metrics-data__title">{totalOrders}</p>
                        <p className="c-metrics-data__label">Current Year</p>
                      </div>
                      <div className="c-metrics-data c-metrics-data--minor">
                        <p className="c-metrics-data__title">{lastFiscalYearOrders}</p>
                        <p className="c-metrics-data__label">
                          Last Year{' '}
                          <Tippy content="Full Year’s Performance">
                            <span className="c-tooltip">
                              <Icon name="help" className="o-svg-icon" />
                            </span>
                          </Tippy>
                        </p>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>

              <hr className="c-order-summary__divider" />

              <table className="c-order-summary-table">
                <thead>
                  <tr>
                    <th className="c-order-summary-table__column c-order-summary-table__column--first">Locations</th>
                    <th className="c-order-summary-table__column">Orders No.</th>
                    <th className="c-order-summary-table__column c-order-summary-table__column--wider">Average Size</th>
                    <th className="c-order-summary-table__column c-order-summary-table__column--wide">Goal Achieved</th>
                    <th className="c-order-summary-table__column c-order-summary-table__column--wider">Total Sales</th>
                  </tr>
                </thead>

                <tbody>
                  {goals.map((goal) => {
                    const territoryFullName =
                      goal.territory && goal.territory.region
                        ? `${goal.territory.region.region_name} > ${goal.territory.territory_name}`
                        : undefined;

                    const percentGoalAchieved =
                      Number(goal.percent_goal_achieved) > 0 ||
                      (Number(goal.percent_goal_achieved) === 0 && Number(goal.sales_goal) > 0)
                        ? Number(goal.percent_goal_achieved)
                        : Infinity;

                    return (
                      <tr className="c-order-summary-table__row" key={goal.id}>
                        <td className="c-order-summary-table__column c-order-summary-table__column--first">
                          <div className="c-order-summary-item">
                            <div>
                              <p className="c-order-summary-item__title">{goal.name}</p>
                              {!!territoryFullName && (
                                <p className="c-order-summary-item__subtitle">{territoryFullName}</p>
                              )}
                            </div>
                          </div>
                        </td>

                        <td className="c-order-summary-table__column">
                          <span className="c-order-summary-table__label">Orders No.</span> {goal.total_orders}
                        </td>

                        <td className="c-order-summary-table__column">
                          <span className="c-order-summary-table__label">Average Size</span>{' '}
                          <DisplayPrice value={Number(goal.average_order_size)} />
                        </td>

                        <td className="c-order-summary-table__column">
                          <span className="c-order-summary-table__label">Goal Achieved</span>{' '}
                          <span className={classNames({ 'u-text-success': Number.isFinite(percentGoalAchieved) })}>
                            {Number.isFinite(percentGoalAchieved)
                              ? `${(percentGoalAchieved * 100).toFixed(2)}%`
                              : 'N/A'}
                          </span>
                        </td>

                        <td className="c-order-summary-table__column">
                          <span className="c-order-summary-table__label">Total Sales</span>
                          <strong>
                            <DisplayPrice value={Number(goal.total_revenue)} />
                          </strong>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default function AgencySalesGoalReportPage() {
  const user = useAuthenticatedUser();
  const { id } = useParams<{ id?: string }>();
  const ability = useAbility();
  const [agencyRes, getAgencyState] = useQuery(api.agency.getAgency, { id: Number(id) });
  useDocumentTitle(agencyRes?.data.name ? `${agencyRes?.data.name} Agency Report` : 'Agency Report');

  const canViewAgenciesList = ability.can('list', 'Agency');

  const showTabs =
    (user.role === AdminUserRoleEnum.Admin || user.role === SalesRepUserRoleEnum.SalesRep) &&
    typeof agencyRes !== 'undefined' &&
    getAgencyState.isSuccess;

  return (
    <>
      <MainHero />

      <section className="c-block c-block--hero c-block--spacing-b-min">
        <div className="o-container-fluid">
          <div className="o-row u-flex-grow u-items-end">
            <div className="o-col-12">
              {canViewAgenciesList && (
                <Link
                  to="/agencies"
                  className="c-link-cta-basic c-link-cta--small c-block__get-back u-mt-spacer-base-large u-mb-spacer-base-small"
                >
                  <Icon name="arrow" className="o-svg-icon o-svg-left" />
                  <span>
                    {user.role === AgencyRepUserRoleEnum.AgencyRep ? 'Back to agency reports' : 'Back to agencies'}
                  </span>
                </Link>
              )}
              <h1 className="c-title--medium u-mb-0">Agency Report</h1>
            </div>
          </div>
          {showTabs && <AgencyTabsNav agency={agencyRes.data} activeTab="sales-goal-report" />}
        </div>
        <div className="c-block__pattern c-block__pattern--light3 c-block__pattern--top3 c-block__overlay--opacity-20">
          <Icon name="pattern" className="o-svg-icon" />
        </div>
      </section>

      {getAgencyState.isLoading && (
        <div className="c-listing__none">
          <div className="c-listing__none-spinner"></div>
          <p className="c-listing__none-title">Loading...</p>
        </div>
      )}
      {getAgencyState.isError && (
        <div className="o-container-fluid u-pb-spacer-section">
          <div className="o-row u-flex-grow u-items-center">
            <div className="o-col-12">
              <div className="c-listing__none">
                <div className="c-listing__none-figure">
                  <Icon name="error" className="o-svg-icon" />
                </div>
                <p className="c-listing__none-title">Error</p>
                <ErrorMessagesContent error={getAgencyState.error} />
              </div>
            </div>
          </div>
        </div>
      )}
      {typeof agencyRes !== 'undefined' && getAgencyState.isSuccess && (
        <>
          {ability.can('read', 'AgencySalesGoalReport') ? (
            <AgencySalesGoalReport agency={agencyRes.data} />
          ) : (
            <Block403Error />
          )}
        </>
      )}
    </>
  );
}
